import { ComponentProps } from '@stitches/react'
import { CombinedError } from 'urql'
import { usePrevious } from 'react-use'
import { useEffect, useRef } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import { Post } from 'models/Post'
import { CommentField } from 'common/Inputs/CommentField'
import { Form } from 'primitives/Form'
import { toast } from 'components/common/Toast'
import { SnackBar } from 'common/SnackBar'
import snackBar from 'common/SnackBar/config'
import { useStore } from 'lib/store'
import { isGraphQLError } from 'lib/urql/errors'
import { ErrorCodes } from 'config/errorCodes'
import { theme } from 'lib/theme'

interface FormValues extends FieldValues {
  comment: string
}

export interface AddCommentProps extends ComponentProps<typeof Form> {
  post: Post
  onAddComment?: (comment: string) => void
}

export const AddComment: React.FC<AddCommentProps> = ({ post, onAddComment, css }) => {
  const store = useStore()
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>()

  const commentFieldRef = useRef<HTMLInputElement | null>()

  const { ref: commentRegisterRef, ...rest } = register('comment', {
    required: ''
  })

  const onSubmit = handleSubmit(async ({ comment }) => {
    if (store.ui.handleGatedAction()) return
    if (comment == '') return

    // Weird thing to reset the <textarea> row height
    commentFieldRef.current?.setAttribute('style', '')
    reset()
    try {
      await post.addComment(comment, store.user)
      onAddComment?.(comment)
    } catch (err) {
      // todo: CombinedError class for throw type (adapt APIError.ts)
      let errorMessage = ''
      switch (true) {
        case isGraphQLError(err as CombinedError, ErrorCodes.MAX_COMMENT_LEN):
          errorMessage = 'Max comment length 255 characters'
          break
        case isGraphQLError(err as CombinedError, ErrorCodes.UTF8_ONLY_COMMENT):
          errorMessage = 'Comment contains an invalid character'
          break
        default:
          errorMessage = 'Unable to post comment'
      }

      // toast(() => <SnackBar title={errorMessage} />, snackBar)
    }
  })

  return (
    <Form css={{ width: '100%', ...css }} onSubmit={onSubmit}>
      <CommentField
        ref={(e) => {
          commentRegisterRef(e)
          commentFieldRef.current = e
        }}
        css={{
          padding: '28px 16px',
          '@md': {
            padding: 0,
            margin: '24px 0'
          },
          '::placeholder': {
            color: '$gray800',
            font: theme.fonts.primary,
            fontSize: '13px'
          }
        }}
        disabled={isSubmitting}
        placeholder="Add a comment.."
        onKeyDown={(e) => {
          // Allows user to line break, remove !e.shiftKey if we want to stop this
          if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
            e.preventDefault()
            setValue('comment', (e.target as HTMLTextAreaElement).value || '')
            onSubmit()
          }
        }}
        onSend={onSubmit}
        error={'comment' in errors && errors?.comment?.message}
        onClick={() => {
          store.ui.handleGatedAction()
        }}
        {...rest}
      />
    </Form>
  )
}
